import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';
import { getApiErrorMessage } from '../utils/errorUtils';
import { identifyUser, clearUserIdentity } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);

      const controller = new AbortController();
      api.get('/auth/me', { signal: controller.signal })
        .then((response) => {
          setUser(response.data);
          setIsAuthenticated(true);
          identifyUser(response.data);
        })
        .catch((error) => {
          if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });

      return () => controller.abort();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setIsAuthenticated(true);

      // Identify user in analytics after login
      identifyUser(user);

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Login failed'),
      };
    }
  };

  const register = async (firstName, lastName, email, password) => {
    try {

      const response = await api.post('/auth/register', {
        first_name: firstName,
        last_name: lastName,
        email,
        password
      });

      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setIsAuthenticated(true);

      // Identify user in analytics after registration
      identifyUser(user);

      return { success: true, message: response.data.message, user };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Registration failed'),
      };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/update-profile', profileData);
      const updatedUser = response.data.user;
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Failed to update profile'),
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Failed to change password'),
      };
    }
  };

  const resendVerification = async () => {
    try {
      const response = await api.post('/auth/resend-verification');
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error, 'Failed to resend verification email'),
      };
    }
  };

  const logout = async () => {
    // Best-effort server-side logout to blacklist the token
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // Ignore errors - we're logging out anyway
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    // Clear user identity in analytics
    clearUserIdentity();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        resendVerification,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

